import { Button } from "@/components/ui/button";
import { useState } from "react";
import { motion } from "framer-motion";
import { ScrollArea } from "../ui/scroll-area";
import Image from "next/image";
import { Check, ThumbsUp } from "lucide-react";
import AnimatedCounter from "./animatedCount";

interface VoteCardProps {
  imageSrc: string;
  votes: number;
  onVote: () => void;
  name: string;
  content: string;
  isVoted: boolean;
  votedName: string;
}

export function VoteCard({
  votes,
  onVote,
  name,
  content,
  isVoted,
  votedName,
}: VoteCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  // const loading = false;
  // const isToggled = false;

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const getButtonText = () => {
    if (!isVoted) return "Cast My Vote";
    if (votedName === name) return "Already Voted";
    return "Change My Vote";
  };

  return (
    <motion.div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      // whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className={`group relative bg-gradient-to-b from-white to-slate-50 dark:from-slate-800 dark:to-slate-900 w-auto h-[420px] backdrop-blur-md shadow-2xl rounded-3xl border border-slate-100 dark:border-slate-700 overflow-hidden my-8 sm:w-80 mx-auto flex flex-col`}

      // className={`group relative bg-gradient-to-b from-white to-slate-50 dark:from-slate-800 dark:to-slate-900 w-auto h-[420px] backdrop-blur-md shadow-2xl rounded-3xl border border-slate-100 dark:border-slate-700 overflow-hidden my-8 sm:w-80 mx-auto flex flex-col`}
    >
      {/* Background accent elements */}
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-400/20 dark:bg-blue-500/10 rounded-full blur-xl"></div>
      <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-indigo-400/20 dark:bg-indigo-500/10 rounded-full blur-xl"></div>

      {/* Card content */}
      <div className="flex flex-col h-full z-10">
        {/* Image container with styled frame */}
        <div className="relative w-full overflow-hidden h-52">
          {/* Gradient overlays for image */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent z-10"></div>
          {/* Main image with animation */}
          <Image
            src={`https://api.catdevai.com/static/token_gif_web/${name}.gif`}
            alt={name}
            fill
            sizes="(max-width: 768px) 100vw, 320px"
            className="absolute inset-0 object-cover h-full w-full transition-all duration-1000 group-hover:scale-105 group-hover:rotate-1"
          />

          {/* Name banner overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 z-20">
            <h2 className="text-white text-xl font-bold text-shadow">{name}</h2>
          </div>

          {/* Vote counter pill */}
          <div className="absolute top-4 right-4 flex items-center justify-center space-x-1 text-sm bg-white/90 dark:bg-slate-800/90 text-indigo-600 dark:text-indigo-400 font-semibold rounded-full shadow-lg z-20 px-3 py-1.5 backdrop-blur-sm border border-white/50 dark:border-slate-700/50">
            <ThumbsUp className="w-5 h-5"/>
            <span className="font-lato flex gap-x-[2px] text-slate-700 dark:text-slate-300">
              <AnimatedCounter to={votes} duration={0.3} /> <span>votes</span>
            </span>
          </div>
        </div>

        {/* Content area with description */}
        <div className="flex-1 p-5 flex flex-col justify-between">
          {/* Description area with scroll */}
          <div className="mb-4">
            <ScrollArea className="h-24 w-full rounded-lg pr-2">
              <motion.p
                initial={{ opacity: 0.85 }}
                animate={{
                  opacity: isHovered ? 1 : 0.85,
                }}
                transition={{ duration: 0.4 }}
                className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed"
              >
                {content}
              </motion.p>
            </ScrollArea>
          </div>

          {/* Vote button area */}
          <motion.div whileTap={{ scale: 0.97 }} className="mt-auto">
            {votedName !== name ? (
              <div className="relative group overflow-hidden rounded-xl">
                {/* Button glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-600 rounded-xl blur-md opacity-40 group-hover:opacity-75 transition duration-500 group-hover:duration-200"></div>

                <Button
                  onClick={onVote}
                  className="relative w-full h-14 bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-600 text-white rounded-xl overflow-hidden transition-all duration-300 font-medium  group-hover:shadow-lg group-hover:shadow-blue-500/25"
                >
                  {/* Animated particles */}
                  <div className="absolute inset-0">
                    <div className="absolute h-3 w-3 rounded-full bg-white/60 top-2 left-[10%] animate-pulse"></div>
                    <div
                      className="absolute h-2 w-2 rounded-full bg-white/60 top-8 left-[15%] animate-pulse"
                      style={{ animationDelay: "300ms" }}
                    ></div>
                    <div
                      className="absolute h-2 w-2 rounded-full bg-white/60 top-3 left-[89%] animate-pulse"
                      style={{ animationDelay: "700ms" }}
                    ></div>
                    <div
                      className="absolute h-3 w-3 rounded-full bg-white/60 top-7 left-[84%] animate-pulse"
                      style={{ animationDelay: "500ms" }}
                    ></div>
                  </div>

                  {/* Button shine effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-all duration-1000 ease-in-out"></div>

                  {/* Button text */}
                  <span className="relative z-10 font-bold tracking-wide flex items-center justify-center text-base">
                    {!isVoted && (
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    )}
                    {votedName && (
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                        />
                      </svg>
                    )}
                    {getButtonText()}
                  </span>
                </Button>
              </div>
            ) : (
              <div className="relative overflow-hidden rounded-xl">
                <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-lime-500 rounded-xl blur-md opacity-30"></div>
                <Button
                  className="relative w-full h-14 bg-gradient-to-r from-green-500 to-lime-500 text-black rounded-xl font-medium flex items-center justify-center"
                  disabled
                >
                  <Check />
                  <span className="font-bold">{getButtonText()}</span>
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
